// bluaIQ — Material Ordering Schedule
// AI-powered alerts telling builders WHEN to order each material based on:
// 1. Project timeline (from Gantt/lifecycle)
// 2. Lead times per material category
// 3. Buffer days for delays
// Shows a timeline of upcoming orders with statuses

import { apiFetch } from '../utils/api'
import { useState, useEffect } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Truck, Clock, AlertTriangle, Check, ChevronDown,
  Calendar, Bell, Download, ArrowRight, ShoppingCart,
  Phone, Mail, MessageSquare, Plus, Pencil, Info
} from 'lucide-react'
import { formatMoneyValue } from '../utils/money'
import { downloadCsv } from '../utils/exportCsv'
import { isDemoProject, DEMO_CONTACTS } from '../utils/demoData'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'

// ── TYPES ─────────────────────────────────────────

interface MaterialOrder {
  id: string
  material: string
  materialEs: string
  category: string
  supplier: string
  supplierPhone: string
  /** When the material is needed on site */
  neededDate: string
  /** Lead time in business days */
  leadTimeDays: number
  /** Buffer days for potential delays */
  bufferDays: number
  /** Calculated: when to place the order */
  orderByDate: string
  /** Days from today until order-by date. Negative = overdue */
  daysUntilOrder: number
  status: 'draft' | 'quote-requested' | 'quoted' | 'ordered' | 'delivered' | 'issue' | 'blocked'
  /** Raw DB status string — only set for real (non-demo) orders. Used by the manual status dropdown. */
  statusDb?: string
  estimatedCost: number | null
  notes?: string
  notesEs?: string
  /** Phase of construction this belongs to */
  phase: string
  phaseEs: string
}

// DB-backed order shape returned by GET/POST/PATCH /material-orders
interface DBMaterialOrder {
  id: string
  projectId: string
  material: string
  category: string
  quantity: number
  unit: string
  supplierContactId: string | null
  supplierContact: {
    id: string
    name: string
    email: string | null
    phone: string | null
    role: string
    company: string | null
  } | null
  neededByDate: string | null
  notes: string | null
  status: string
  sourceType: string
  createdAt: string
  updatedAt: string
}

// ── DEMO DATA (1,800 sqft residential — DFW timeline) ──

const DEMO_ORDERS: MaterialOrder[] = [
  // ── FOUNDATION PHASE (Week 1-2) ──
  {
    id: 'mo-1', material: 'Concrete (Foundation)', materialEs: 'Concreto (Cimentación)',
    category: 'FOUNDATION', supplier: 'TXI Concrete - DFW', supplierPhone: '(972) 555-0101',
    neededDate: '2026-04-07', leadTimeDays: 3, bufferDays: 1, orderByDate: '2026-04-01',
    daysUntilOrder: 8, status: 'draft', estimatedCost: 4200,
    notes: '28 yards @ $155/yd — confirm pump truck', notesEs: '28 yardas @ $155/yd — confirmar bomba',
    phase: 'Foundation', phaseEs: 'Cimentación',
  },
  {
    id: 'mo-2', material: 'Rebar + Wire Mesh', materialEs: 'Varilla + Malla electrosoldada',
    category: 'FOUNDATION', supplier: 'CMC Steel - Dallas', supplierPhone: '(214) 555-0202',
    neededDate: '2026-04-05', leadTimeDays: 2, bufferDays: 1, orderByDate: '2026-03-31',
    daysUntilOrder: 7, status: 'draft', estimatedCost: 1800,
    phase: 'Foundation', phaseEs: 'Cimentación',
  },
  {
    id: 'mo-3', material: 'Plumbing Rough-In (underslab)', materialEs: 'Plomería bajo losa',
    category: 'PLUMBING', supplier: 'Ferguson Plumbing', supplierPhone: '(817) 555-0303',
    neededDate: '2026-04-04', leadTimeDays: 5, bufferDays: 2, orderByDate: '2026-03-26',
    daysUntilOrder: 2, status: 'quote-requested', estimatedCost: 3500,
    notes: 'PEX manifold system — verify layout with plumber', notesEs: 'Sistema PEX manifold — verificar layout con plomero',
    phase: 'Foundation', phaseEs: 'Cimentación',
  },

  // ── FRAMING PHASE (Week 3-5) ──
  {
    id: 'mo-4', material: 'Lumber Package (framing)', materialEs: 'Paquete de madera (estructura)',
    category: 'FRAMING', supplier: '84 Lumber - Fort Worth', supplierPhone: '(817) 555-0404',
    neededDate: '2026-04-14', leadTimeDays: 7, bufferDays: 3, orderByDate: '2026-04-01',
    daysUntilOrder: 8, status: 'draft', estimatedCost: 18500,
    notes: '2x4, 2x6, 2x10, LVL beams, OSB sheathing — full package', notesEs: '2x4, 2x6, 2x10, vigas LVL, OSB — paquete completo',
    phase: 'Framing', phaseEs: 'Estructura',
  },
  {
    id: 'mo-5', material: 'Engineered Trusses', materialEs: 'Armaduras de techo',
    category: 'FRAMING', supplier: 'Truss Systems Inc', supplierPhone: '(972) 555-0505',
    neededDate: '2026-04-21', leadTimeDays: 14, bufferDays: 3, orderByDate: '2026-03-31',
    daysUntilOrder: 7, status: 'draft', estimatedCost: 5200,
    notes: '14-day lead time! Order ASAP', notesEs: '14 días de anticipación! Ordenar lo antes posible',
    phase: 'Framing', phaseEs: 'Estructura',
  },
  {
    id: 'mo-6', material: 'Windows (vinyl, low-E)', materialEs: 'Ventanas (vinil, low-E)',
    category: 'FRAMING', supplier: 'Milgard - Dallas', supplierPhone: '(214) 555-0606',
    neededDate: '2026-04-28', leadTimeDays: 21, bufferDays: 5, orderByDate: '2026-03-27',
    daysUntilOrder: 3, status: 'quote-requested', estimatedCost: 6800,
    notes: '21-day lead — 14 windows, verify sizes after framing', notesEs: '21 días de anticipación — 14 ventanas, verificar medidas después de estructura',
    phase: 'Framing', phaseEs: 'Estructura',
  },

  // ── MECHANICAL (Week 5-7) ──
  {
    id: 'mo-7', material: 'HVAC System (3.5 ton)', materialEs: 'Sistema HVAC (3.5 ton)',
    category: 'MECHANICAL', supplier: 'Lennox Dealer - Plano', supplierPhone: '(469) 555-0707',
    neededDate: '2026-05-04', leadTimeDays: 10, bufferDays: 3, orderByDate: '2026-04-18',
    daysUntilOrder: 25, status: 'draft', estimatedCost: 8500,
    phase: 'Mechanical', phaseEs: 'Mecánico',
  },
  {
    id: 'mo-8', material: 'Electrical Panel + Wire', materialEs: 'Centro de carga + Cable',
    category: 'ELECTRICAL', supplier: 'Graybar Electric', supplierPhone: '(817) 555-0808',
    neededDate: '2026-05-04', leadTimeDays: 3, bufferDays: 1, orderByDate: '2026-04-29',
    daysUntilOrder: 36, status: 'draft', estimatedCost: 2800,
    phase: 'Mechanical', phaseEs: 'Mecánico',
  },

  // ── EXTERIOR (Week 6-8) ──
  {
    id: 'mo-9', material: 'Roofing (30-yr architectural)', materialEs: 'Techo (arquitectónico 30 años)',
    category: 'ROOFING', supplier: 'ABC Supply - Arlington', supplierPhone: '(682) 555-0909',
    neededDate: '2026-05-11', leadTimeDays: 3, bufferDays: 1, orderByDate: '2026-05-06',
    daysUntilOrder: 43, status: 'draft', estimatedCost: 4800,
    phase: 'Exterior', phaseEs: 'Exterior',
  },
  {
    id: 'mo-10', material: 'Brick + Stone Veneer', materialEs: 'Ladrillo + Piedra',
    category: 'EXTERIOR', supplier: 'Acme Brick - DFW', supplierPhone: '(817) 555-1010',
    neededDate: '2026-05-18', leadTimeDays: 10, bufferDays: 3, orderByDate: '2026-05-04',
    daysUntilOrder: 41, status: 'draft', estimatedCost: 7200,
    notes: 'Color selection needed — schedule showroom visit', notesEs: 'Se necesita selección de color — agendar visita a showroom',
    phase: 'Exterior', phaseEs: 'Exterior',
  },

  // ── INTERIOR FINISH (Week 9-12) ──
  {
    id: 'mo-11', material: 'Drywall + Supplies', materialEs: 'Tablaroca + Suministros',
    category: 'DRYWALL', supplier: 'USG / L&W Supply', supplierPhone: '(972) 555-1111',
    neededDate: '2026-05-25', leadTimeDays: 3, bufferDays: 1, orderByDate: '2026-05-20',
    daysUntilOrder: 57, status: 'draft', estimatedCost: 3200,
    phase: 'Interior Finish', phaseEs: 'Acabados Interiores',
  },
  {
    id: 'mo-12', material: 'Kitchen Cabinets', materialEs: 'Gabinetes de Cocina',
    category: 'CABINETS', supplier: 'CKF Cabinets - Dallas', supplierPhone: '(214) 555-1212',
    neededDate: '2026-06-08', leadTimeDays: 28, bufferDays: 5, orderByDate: '2026-05-04',
    daysUntilOrder: 41, status: 'draft', estimatedCost: 8000,
    notes: '28-day lead! Must order early — requires final kitchen design', notesEs: '28 días de anticipación! Ordenar temprano — requiere diseño de cocina final',
    phase: 'Interior Finish', phaseEs: 'Acabados Interiores',
  },
  {
    id: 'mo-13', material: 'Countertops (Granite L1)', materialEs: 'Cubiertas (Granito N1)',
    category: 'COUNTERTOPS', supplier: 'Texas Countertops', supplierPhone: '(469) 555-1313',
    neededDate: '2026-06-15', leadTimeDays: 14, bufferDays: 3, orderByDate: '2026-05-27',
    daysUntilOrder: 64, status: 'draft', estimatedCost: 2500,
    notes: 'Template after cabinets installed', notesEs: 'Plantilla después de instalar gabinetes',
    phase: 'Interior Finish', phaseEs: 'Acabados Interiores',
  },
  {
    id: 'mo-14', material: 'Flooring (LVP + Carpet)', materialEs: 'Pisos (LVP + Alfombra)',
    category: 'FLOORING', supplier: 'Floor & Decor - DFW', supplierPhone: '(972) 555-1414',
    neededDate: '2026-06-15', leadTimeDays: 5, bufferDays: 2, orderByDate: '2026-06-06',
    daysUntilOrder: 74, status: 'draft', estimatedCost: 5500,
    phase: 'Interior Finish', phaseEs: 'Acabados Interiores',
  },
  {
    id: 'mo-15', material: 'Interior Doors + Hardware', materialEs: 'Puertas Interiores + Herrajes',
    category: 'DOORS', supplier: '84 Lumber - Fort Worth', supplierPhone: '(817) 555-0404',
    neededDate: '2026-06-08', leadTimeDays: 7, bufferDays: 2, orderByDate: '2026-05-28',
    daysUntilOrder: 65, status: 'draft', estimatedCost: 1900,
    phase: 'Interior Finish', phaseEs: 'Acabados Interiores',
  },

  // ── FINAL (Week 12-14) ──
  {
    id: 'mo-16', material: 'Light Fixtures Package', materialEs: 'Paquete de Luminarias',
    category: 'ELECTRICAL', supplier: 'Home Depot Pro', supplierPhone: '(817) 555-1616',
    neededDate: '2026-06-22', leadTimeDays: 5, bufferDays: 2, orderByDate: '2026-06-13',
    daysUntilOrder: 81, status: 'draft', estimatedCost: 1500,
    phase: 'Final', phaseEs: 'Final',
  },
  {
    id: 'mo-17', material: 'Plumbing Fixtures (toilets, faucets)', materialEs: 'Muebles de baño (sanitarios, llaves)',
    category: 'PLUMBING', supplier: 'Ferguson Plumbing', supplierPhone: '(817) 555-0303',
    neededDate: '2026-06-22', leadTimeDays: 5, bufferDays: 2, orderByDate: '2026-06-13',
    daysUntilOrder: 81, status: 'draft', estimatedCost: 2200,
    notes: 'Match with Finish Selections choices', notesEs: 'Coincidir con selecciones de acabados',
    phase: 'Final', phaseEs: 'Final',
  },
  {
    id: 'mo-18', material: 'Garage Door + Opener', materialEs: 'Puerta de Garage + Motor',
    category: 'EXTERIOR', supplier: 'Overhead Door - DFW', supplierPhone: '(214) 555-1818',
    neededDate: '2026-06-29', leadTimeDays: 10, bufferDays: 3, orderByDate: '2026-06-13',
    daysUntilOrder: 81, status: 'draft', estimatedCost: 1500,
    phase: 'Final', phaseEs: 'Final',
  },
  {
    id: 'mo-19', material: 'Appliance Package', materialEs: 'Paquete de Electrodomésticos',
    category: 'APPLIANCES', supplier: 'Lowe\'s Pro - Plano', supplierPhone: '(469) 555-1919',
    neededDate: '2026-07-06', leadTimeDays: 7, bufferDays: 3, orderByDate: '2026-06-24',
    daysUntilOrder: 92, status: 'draft', estimatedCost: 4200,
    notes: 'Coordinate delivery with final walkthrough', notesEs: 'Coordinar entrega con recorrido final',
    phase: 'Final', phaseEs: 'Final',
  },

  // ── ALREADY ORDERED (demo) ──
  {
    id: 'mo-20', material: 'Survey + Staking', materialEs: 'Levantamiento + Estacado',
    category: 'SITE PREP', supplier: 'Metroplex Surveying', supplierPhone: '(972) 555-2020',
    neededDate: '2026-03-28', leadTimeDays: 5, bufferDays: 1, orderByDate: '2026-03-20',
    daysUntilOrder: -4, status: 'ordered', estimatedCost: 1200,
    phase: 'Site Prep', phaseEs: 'Preparación del Sitio',
  },
  {
    id: 'mo-21', material: 'Temporary Power Pole', materialEs: 'Poste de Luz Temporal',
    category: 'SITE PREP', supplier: 'Oncor / Electrician', supplierPhone: '(817) 555-2121',
    neededDate: '2026-03-26', leadTimeDays: 10, bufferDays: 2, orderByDate: '2026-03-12',
    daysUntilOrder: -12, status: 'delivered', estimatedCost: 800,
    phase: 'Site Prep', phaseEs: 'Preparación del Sitio',
  },
]

// ── STATUS CONFIG ─────────────────────────────────

// PR #193: full 7-status config covering the complete procurement lifecycle.
// Statuses match MaterialOrder.status DB values (lowercased + hyphenated for FE).
// CANCELLED maps to 'draft' as a defensive fallback in the DB→FE mapping and
// does not need its own CONFIG entry.
const STATUS_CONFIG = {
  draft: { color: '#9E9890', bg: 'bg-[#F2F0EB]', border: 'border-[#ECEAE4]', label: 'DRAFT', labelEs: 'BORRADOR', icon: Clock },
  'quote-requested': { color: '#06539B', bg: 'bg-blue-50', border: 'border-blue-200', label: 'QUOTE REQUESTED', labelEs: 'COTIZACIÓN SOLICITADA', icon: Mail },
  quoted: { color: '#7C3AED', bg: 'bg-purple-50', border: 'border-purple-200', label: 'QUOTED', labelEs: 'COTIZADO', icon: MessageSquare },
  ordered: { color: '#00BCD4', bg: 'bg-cyan-50', border: 'border-cyan-200', label: 'ORDERED', labelEs: 'ORDENADO', icon: Check },
  delivered: { color: '#22C55E', bg: 'bg-green-50', border: 'border-green-200', label: 'DELIVERED', labelEs: 'ENTREGADO', icon: Check },
  issue: { color: '#F59E0B', bg: 'bg-amber-50', border: 'border-amber-200', label: 'ISSUE', labelEs: 'PROBLEMA', icon: AlertTriangle },
  blocked: { color: '#EF4444', bg: 'bg-red-50', border: 'border-red-200', label: 'BLOCKED', labelEs: 'BLOQUEADO', icon: AlertTriangle },
}

// ── MATERIAL ORDER FORM OPTIONS ───────────────────────────────────────────────
// PR #192: shared between create and edit modals. category and unit are both
// unconstrained String columns in the DB schema — no migration is needed when
// adding values to either list.

const MATERIAL_CATEGORIES = [
  'FOUNDATION', 'FRAMING', 'ROOFING', 'EXTERIOR', 'WINDOWS', 'DOORS',
  'PLUMBING', 'ELECTRICAL', 'MECHANICAL', 'INSULATION', 'DRYWALL',
  'CABINETS', 'COUNTERTOPS', 'FLOORING', 'FINISHES', 'APPLIANCES',
  'HARDWARE', 'SITEWORK', 'CONCRETE', 'OTHER',
]

const MATERIAL_UNITS = [
  'EA', 'LF', 'SF', 'CY', 'BF', 'BAG', 'TON', 'GAL', 'LS', 'LOT', 'PKG',
]

// ── SUPPLIER-EMAIL DRAFT GENERATOR (pure helper) ──────────────────
//
// PR-NEW: builds a bilingual material-order email draft from
// already-known data — no AI call, no backend hit. Used by the
// per-order "Create supplier email" form. Tests cover both
// languages and the bilingual fallbacks for missing supplier
// name + missing project name. Never substitutes a Trial /
// Demo / Test / undefined / null placeholder for the supplier
// or project name — the fallback is always the localized "this
// project" / "este proyecto" string.

export interface SupplierEmailDraftInput {
  projectName?: string | null
  supplierName?: string | null
  materialName: string
  notesOrQuantity?: string | null
  onSiteDate?: string | null
  orderByDate?: string | null
}
export interface SupplierEmailDraft {
  subject: string
  body: string
}

function safeProjectName(raw: string | null | undefined, isEs: boolean): string {
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  if (!trimmed) return isEs ? 'este proyecto' : 'this project'
  const lower = trimmed.toLowerCase()
  // Reject sentinel-y values that would leak through into emails.
  if (
    lower === 'undefined' ||
    lower === 'null' ||
    lower === 'trial user' ||
    lower === 'demo user' ||
    lower === 'test user'
  ) {
    return isEs ? 'este proyecto' : 'this project'
  }
  return trimmed
}

function safeSupplierGreeting(raw: string | null | undefined, isEs: boolean): string {
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  if (!trimmed) return isEs ? 'Estimado/a,' : 'Hello,'
  const lower = trimmed.toLowerCase()
  if (
    lower === 'undefined' ||
    lower === 'null' ||
    lower === 'trial user' ||
    lower === 'demo user' ||
    lower === 'test user'
  ) {
    return isEs ? 'Estimado/a,' : 'Hello,'
  }
  return isEs ? `Estimado/a ${trimmed},` : `Hello ${trimmed},`
}

function safeLine(raw: string | null | undefined, isEs: boolean): string {
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  if (!trimmed) return isEs ? 'a coordinar' : 'to be confirmed'
  return trimmed
}

export function buildSupplierEmailDraft(
  input: SupplierEmailDraftInput,
  isEs: boolean,
): SupplierEmailDraft {
  const projectName = safeProjectName(input.projectName, isEs)
  const greeting = safeSupplierGreeting(input.supplierName, isEs)
  const material = (input.materialName ?? '').trim() || (isEs ? 'material por definir' : 'material to be defined')
  const notes = safeLine(input.notesOrQuantity, isEs)
  const onSite = safeLine(input.onSiteDate, isEs)
  const orderBy = safeLine(input.orderByDate, isEs)

  if (isEs) {
    return {
      subject: `Pedido de materiales — ${projectName}`,
      body: [
        greeting,
        '',
        `Estamos solicitando el siguiente material para el proyecto ${projectName}:`,
        '',
        `- ${material}`,
        `Cantidad/alcance: ${notes}`,
        `Requerido en obra: ${onSite}`,
        `Ordenar antes de: ${orderBy}`,
        '',
        'Favor de confirmar disponibilidad, precio y tiempo de entrega.',
        '',
        'Saludos.',
      ].join('\n'),
    }
  }
  return {
    subject: `Material order request — ${projectName}`,
    body: [
      greeting,
      '',
      `We are requesting the following material for project ${projectName}:`,
      '',
      `- ${material}`,
      `Quantity/scope: ${notes}`,
      `Needed on site: ${onSite}`,
      `Order by: ${orderBy}`,
      '',
      'Please confirm availability, price, and delivery time.',
      '',
      'Thank you.',
    ].join('\n'),
  }
}

// ── COMPONENT ─────────────────────────────────────

export default function MaterialOrderSchedule() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  // Phase 1 — honor the `?category=X` deep-link contract documented
  // in App.tsx (alias for Calendar's 📦 reminder links). Read-only:
  // we never write the param back. When present, it filters the
  // schedule to a single category as a soft initial scope. The
  // user can clear it via the small dismissable pill rendered near
  // the header. The schedule remains user-driven throughout —
  // bluaIQ assists, it does not decide.
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryFromQuery = searchParams.get('category')
  // Supplier-selection handoff (FE-only).
  // When the user picks a contact in ContactsModule via the
  // "Use this supplier" button, Contacts navigates back here with
  //   ?selectedSupplierId=<contactId>&orderId=<orderId>[&category=…]
  // We consume both params in a one-shot useEffect below: write
  // `orderFrom[orderId] = contactId`, auto-fill `supplierEmailById
  // [orderId]` only when the user has not typed something, expand
  // the targeted order so the picked supplier is visible, surface
  // a small bilingual confirmation pill, then strip the two
  // selection params from the URL so a refresh doesn't re-apply
  // the same selection. The handoff is pure LOCAL FE state — no
  // backend, no order creation, no email send, no purchase action.
  const selectedSupplierIdParam = searchParams.get('selectedSupplierId')
  const orderIdParam = searchParams.get('orderId')
  // PR #138B: demo projects have no DB row + no real supplier
  // emails. POST /material-orders/email-thread would create a real
  // outbound thread against demo data — block that and the local
  // markOrdered toggle at the action-bar level.
  const isDemo = isDemoProject(projectId)

  // PR #189: real projects fetch persisted orders from the API.
  // Demo projects continue to use DEMO_ORDERS (local state only).
  const { data: dbOrders, isLoading: ordersLoading } = useQuery<DBMaterialOrder[]>({
    queryKey: ['material-orders', projectId],
    queryFn: async () => {
      if (!projectId) return []
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/material-orders`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('Failed to load orders')
      return res.json()
    },
    enabled: !!projectId && !isDemo,
    staleTime: 30_000,
  })

  // For demo: local DEMO_ORDERS state (unchanged). For real: API-backed.
  const [demoOrders, setDemoOrders] = useState<MaterialOrder[]>(DEMO_ORDERS)
  // Unified orders list: demo uses local state, real uses API result.
  const orders: MaterialOrder[] = isDemo
    ? demoOrders
    : (dbOrders ?? []).map((o): MaterialOrder => ({
        id: o.id,
        material: o.material,
        materialEs: o.material, // real orders use single language field
        category: o.category,
        supplier: o.supplierContact?.name ?? '',
        supplierPhone: o.supplierContact?.phone ?? '',
        neededDate: o.neededByDate ?? '',
        leadTimeDays: 0,
        bufferDays: 0,
        orderByDate: '',
        daysUntilOrder: 0,
        // PR #193: full DB→FE status mapping covering all 7 DB statuses.
        // CANCELLED is not a UI state — fall back to 'draft' defensively.
        status: (
          o.status === 'ORDERED'           ? 'ordered' :
          o.status === 'DELIVERED'         ? 'delivered' :
          o.status === 'QUOTE_REQUESTED'   ? 'quote-requested' :
          o.status === 'QUOTED'            ? 'quoted' :
          o.status === 'ISSUE'             ? 'issue' :
          o.status === 'BLOCKED'           ? 'blocked' :
          'draft' // DRAFT, CANCELLED, or any unknown value
        ) as MaterialOrder['status'],
        statusDb: o.status,
        estimatedCost: null,
        notes: o.notes ?? undefined,
        notesEs: undefined,
        phase: o.category,
        phaseEs: o.category,
      }))

  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [orderFrom, setOrderFrom] = useState<Record<string, string>>({})

  // PR #195: seed orderFrom from persisted supplierContactId when DB orders load.
  // Ensures the "Order from" dropdown reflects the saved supplier after page
  // reload so the RFQ flow works without re-selection.
  // Only for real projects (isDemo guard); does not overwrite active user edits
  // (functional updater only sets entries absent from prev).
  useEffect(() => {
    if (isDemo || !dbOrders || dbOrders.length === 0) return
    setOrderFrom(prev => {
      const patch: Record<string, string> = {}
      for (const o of dbOrders) {
        if (o.supplierContactId && !prev[o.id]) {
          patch[o.id] = o.supplierContactId
        }
      }
      if (Object.keys(patch).length === 0) return prev
      return { ...prev, ...patch }
    })
  }, [dbOrders, isDemo])

  // PR-NEW: per-order supplier-email form state. The supplier
  // email is keyed by order id so each row keeps its own
  // draft / input / status. After PR #114 the primary submit
  // path is the create-thread route — it persists an
  // EmailThread + DRAFT EmailMessage and navigates to the
  // Emails module so the user reviews + sends from there.
  // mailto: stays as a graceful-degradation fallback only.
  const [supplierEmailById, setSupplierEmailById] = useState<Record<string, string>>({})
  const [emailFormOpenId, setEmailFormOpenId] = useState<string | null>(null)
  const [draftByOrderId, setDraftByOrderId] = useState<Record<string, { subject: string; body: string }>>({})
  const [draftToastByOrderId, setDraftToastByOrderId] = useState<Record<string, string | null>>({})
  // PR-NEW: in-flight + error state for the create-thread call.
  const [threadCreatingId, setThreadCreatingId] = useState<string | null>(null)
  const [threadErrorById, setThreadErrorById] = useState<Record<string, string | null>>({})

  // PR #189: create material order modal state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createFormData, setCreateFormData] = useState({
    material: '',
    category: 'FRAMING',
    quantity: '',
    unit: '',
    supplierContactId: '',
    neededByDate: '',
    notes: '',
  })
  const [createError, setCreateError] = useState<string | null>(null)
  const [createLoading, setCreateLoading] = useState(false)

  // PR #192: edit material order modal state
  const [showEditForm, setShowEditForm] = useState(false)
  const [editOrderId, setEditOrderId] = useState<string | null>(null)
  const [editFormData, setEditFormData] = useState({
    material: '',
    category: 'FRAMING',
    quantity: '',
    unit: '',
    supplierContactId: '',
    neededByDate: '',
    notes: '',
  })
  const [editError, setEditError] = useState<string | null>(null)
  const [editLoading, setEditLoading] = useState(false)

  // PR #193: tracks which order has an in-flight manual status PATCH.
  // Gated by !isDemo — demo never calls handleSetStatus.
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null)

  // Fetch project name once so the draft template carries
  // "{{projectName}}" instead of falling back to the localized
  // "this project" / "este proyecto" string. Soft-fails: if the
  // request 401s or 404s the draft still generates with the
  // bilingual fallback.
  const { data: projectMeta } = useQuery<{ name?: string } | null>({
    queryKey: ['project-meta', projectId],
    queryFn: async () => {
      if (!projectId) return null
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) return null
      return res.json()
    },
    retry: 0,
    enabled: !!projectId,
  })
  const projectName = (projectMeta?.name ?? '').trim() || null

  // ── Supplier contacts (Contacts module, role === 'SUPPLIER') ─────
  //
  // The "Order from" dropdown used to be hardcoded with Home Depot /
  // Lowe's / 84 Lumber / Local Lumber Yard / Other strings. Real
  // procurement happens with the builder's own suppliers, so we now
  // source the dropdown from `ProjectContact` rows filtered to
  // `role === 'SUPPLIER'`. Same useQuery shape ContactsModule uses
  // (queryKey ['contacts', projectId], GET /api/projects/:id/contacts)
  // so the cache is shared — adding a supplier in Contacts shows up
  // here without an extra refetch.
  //
  // Demo fallback (PR #188): demo projects have no ProjectContact rows
  // in the DB, so the API returns []. When the API result has no
  // SUPPLIER-role entries, demo projects fall back to DEMO_CONTACTS
  // filtered to role === 'SUPPLIER'. This ensures the MO → Contacts →
  // "Use this supplier" handoff works end-to-end for demo without
  // touching any real-project path.
  // Real projects with no supplier contacts remain [] (honest empty state).
  interface SupplierContact {
    id: string
    name: string
    role: string
    phone?: string | null
    email?: string | null
    company?: string | null
    trade?: string | null
  }
  const { data: apiContacts } = useQuery<SupplierContact[]>({
    queryKey: ['contacts', projectId],
    queryFn: async () => {
      if (!projectId) return []
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/contacts`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('API unavailable')
      return res.json()
    },
    retry: false,
    enabled: !!projectId,
  })
  const apiSuppliers: SupplierContact[] = Array.isArray(apiContacts)
    ? apiContacts.filter((c) => (c.role || '').toUpperCase() === 'SUPPLIER')
    : []
  const supplierContacts: SupplierContact[] =
    apiSuppliers.length > 0
      ? apiSuppliers
      : isDemo
        ? (DEMO_CONTACTS.filter((c) => c.role === 'SUPPLIER') as SupplierContact[])
        : []
  // Map for O(1) lookup of selected supplier metadata (name + email).
  const supplierById = new Map<string, SupplierContact>(
    supplierContacts.map((c) => [c.id, c]),
  )

  // Supplier-selection handoff — consumption side.
  // After "Use this supplier" is clicked in ContactsModule, the
  // browser returns to this route with `?selectedSupplierId=…
  // &orderId=…[&category=…]`. We apply the selection to LOCAL FE
  // state only (orderFrom + supplierEmailById), expand the targeted
  // order so the picked supplier is visible in the dropdown, and
  // strip the two selection params from the URL so a refresh does
  // not re-apply the same selection. The category param (PR #167)
  // is preserved if present. Nothing here creates an order, marks
  // an order as ordered, sends an email, or touches the backend.
  const [pickedFromContactsForOrderId, setPickedFromContactsForOrderId] = useState<string | null>(null)
  // Phase 2 — supplier-selection return position.
  // `highlightedOrderId` flashes a subtle emerald ring on the
  // targeted order card for a few seconds after the handoff, so
  // the user can immediately locate the order they were editing
  // before they jumped into Contacts. The state is purely local
  // to this component (no global store), auto-clears via the
  // timer captured below, and is independent of the persistent
  // `pickedFromContactsForOrderId` (the confirmation pill stays
  // until the user dismisses it).
  const [highlightedOrderId, setHighlightedOrderId] = useState<string | null>(null)
  useEffect(() => {
    if (!selectedSupplierIdParam || !orderIdParam) return
    // Wait for the contacts query to resolve so `supplierById` can
    // surface the chosen contact's metadata (mainly the email).
    if (supplierContacts.length === 0) return
    const contact = supplierById.get(selectedSupplierIdParam)
    if (!contact) {
      // Unknown contact id — strip the params silently rather than
      // applying a stale id. Defensive against a user opening a
      // bookmarked URL after the contact was deleted.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('selectedSupplierId')
        next.delete('orderId')
        return next
      }, { replace: true })
      return
    }
    setOrderFrom((prev) => ({ ...prev, [orderIdParam]: selectedSupplierIdParam }))
    setSupplierEmailById((prev) => {
      const existingTyped = (prev[orderIdParam] || '').trim()
      if (existingTyped) return prev
      if (!contact.email) return prev
      return { ...prev, [orderIdParam]: contact.email }
    })
    setExpandedId(orderIdParam)
    setPickedFromContactsForOrderId(orderIdParam)
    // PR #189: persist supplier assignment for real orders
    if (!isDemo && orderIdParam && selectedSupplierIdParam) {
      getToken().then(token => {
        apiFetch(`/api/projects/${projectId}/material-orders/${orderIdParam}`, {
          method: 'PATCH',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ supplierContactId: selectedSupplierIdParam }),
        }).then(() => {
          queryClient.invalidateQueries({ queryKey: ['material-orders', projectId] })
        }).catch(() => {/* best-effort */})
      })
    }
    setHighlightedOrderId(orderIdParam)
    // Schedule a scroll-into-view AFTER React commits the expanded
    // state above. Two rAFs guarantee the layout pass has produced
    // the expanded panel before we scroll, so `scrollIntoView`
    // centers the FULL panel (header + drawer) instead of just
    // the collapsed row. Guarded against `document` being absent
    // (SSR / tests).
    if (typeof document !== 'undefined') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById(`material-order-${orderIdParam}`)
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      })
    }
    // Auto-clear the highlight after a short window. The effect
    // re-runs on each new pick, so any previous timer is rendered
    // moot by the next setHighlightedOrderId(orderIdParam) call.
    // Returning the cleanup keeps timers tidy on unmount.
    const timer = setTimeout(() => setHighlightedOrderId(null), 4000)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('selectedSupplierId')
      next.delete('orderId')
      return next
    }, { replace: true })
    return () => clearTimeout(timer)
    // We only want this effect to run when a new selection arrives
    // from Contacts (i.e. the URL params change). `supplierContacts
    // .length` is in deps so the effect re-runs once the contacts
    // query resolves on first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplierIdParam, orderIdParam, supplierContacts.length])
  // Build the human-readable display label for one supplier dropdown
  // row. Deterministic; no AI; bilingual-safe (the labels themselves
  // are user-entered contact data, not localized strings).
  function buildSupplierLabel(c: SupplierContact): string {
    const parts = [c.name]
    if (c.company && c.company.trim() && c.company.trim() !== c.name.trim()) {
      parts.push(`— ${c.company.trim()}`)
    } else if (c.trade && c.trade.trim()) {
      parts.push(`— ${c.trade.trim()}`)
    }
    return parts.join(' ')
  }

  // PR-NEW: per-order email-thread status surface. Lets the Material
  // Orders schedule badge each row with DRAFT / SENT / REPLIED /
  // CLOSED and swap the CTA accordingly so a builder sees, at a
  // glance, whether they've already started a conversation with the
  // supplier — without leaving the schedule.
  interface ThreadSummaryRow {
    threadId: string
    status: 'DRAFT' | 'SENT' | 'REPLIED' | 'CLOSED' | string
    recipientEmail: string | null
    draftId: string | null
    draftStatus: string | null
    sentAt: string | null
    lastInboundAt: string | null
    closedAt: string | null
    updatedAt: string
  }
  const { data: threadsSummary } = useQuery<Record<string, ThreadSummaryRow>>({
    queryKey: ['material-orders-threads-summary', projectId],
    queryFn: async () => {
      if (!projectId) return {}
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/material-orders/threads-summary`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) return {}
      return res.json()
    },
    retry: 0,
    enabled: !!projectId,
    // Light auto-refresh: while at least one open thread is SENT
    // (awaiting a reply), poll every 60s so a fresh INBOUND row
    // surfaces without manual refresh. No interval when there is
    // nothing to wait on.
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const d = q.state.data as Record<string, ThreadSummaryRow> | undefined
      if (!d) return false
      const hasWaiting = Object.values(d).some((r) => r.status === 'SENT' && !r.lastInboundAt)
      return hasWaiting ? 60_000 : false
    },
  })

  // Localized helper for the per-row status badge + CTA copy.
  // Splitting the lookup keeps the JSX below readable.
  type EmailStatusKind = 'NONE' | 'DRAFT' | 'SENT' | 'REPLIED' | 'CLOSED'
  const kindOf = (row: ThreadSummaryRow | undefined): EmailStatusKind => {
    if (!row) return 'NONE'
    if (row.status === 'DRAFT') return 'DRAFT'
    if (row.status === 'SENT') return 'SENT'
    if (row.status === 'REPLIED') return 'REPLIED'
    if (row.status === 'CLOSED') return 'CLOSED'
    return 'NONE'
  }
  // Bilingual full-phrase badge labels. Short single-word badges
  // ("Sent" / "Enviado") were too easy to miss in production — the
  // user did not realize an email had already gone out. Full phrases
  // ("Email sent" / "Correo enviado") make the state obvious.
  const emailBadgeLabel = (kind: EmailStatusKind): string => {
    switch (kind) {
      case 'DRAFT':    return isEs ? 'Borrador'                : 'Email draft'
      case 'SENT':     return isEs ? 'Correo enviado'          : 'Email sent'
      case 'REPLIED':  return isEs ? 'El proveedor respondió'  : 'Supplier replied'
      // PR-NEW: CLOSED is rendered as "Archived" in user-facing
      // copy to match the email-service vocabulary in EmailsModule.
      case 'CLOSED':   return isEs ? 'Correo archivado'         : 'Archived email'
      default:         return ''
    }
  }
  // Same enum, different presentation: used in the expanded panel
  // alongside the "Sent on …" datetime.
  const emailExpandedHeadline = (kind: EmailStatusKind): string => {
    switch (kind) {
      case 'DRAFT':
        return isEs ? 'Borrador listo para revisar' : 'Draft ready to review'
      case 'SENT':
        return isEs ? 'Correo enviado — esperando respuesta del proveedor' : 'Email sent — waiting for supplier reply'
      case 'REPLIED':
        return isEs ? 'El proveedor respondió' : 'Supplier replied'
      case 'CLOSED':
        return isEs ? 'Correo archivado' : 'Archived email'
      default:
        return ''
    }
  }
  // PR-NEW: CTA labels were too generic ("View conversation").
  // Production spec asks for outcome-specific phrasing so the user
  // knows what they'll see before clicking: a sent email, a
  // supplier reply, or the closed thread.
  const emailCtaLabel = (kind: EmailStatusKind): string => {
    switch (kind) {
      case 'DRAFT':    return isEs ? 'Revisar borrador'              : 'Review draft'
      case 'SENT':     return isEs ? 'Ver correo enviado'            : 'View sent email'
      case 'REPLIED':  return isEs ? 'Ver respuesta del proveedor'   : 'View supplier reply'
      case 'CLOSED':   return isEs ? 'Ver correo'                    : 'View email'
      default:         return isEs ? 'Crear correo en NATAMI'        : 'Create email in NATAMI'
    }
  }

  const openThread = (threadId: string) => {
    navigate(`/projects/${projectId}/emails?thread=${encodeURIComponent(threadId)}`)
  }

  // PR-NEW: primary submit path — create a persistent
  // EmailThread + DRAFT EmailMessage server-side and navigate
  // to /emails?thread=<id> so the user reviews + sends from
  // the Emails module. Inbound replies pair back to this
  // thread via the unique Reply-To address generated at
  // creation. mailto: stays as a graceful fallback only when
  // the request fails (offline / 5xx).
  // Resolve the effective supplier identity for an order at send time.
  // Preference order:
  //   1. A `ProjectContact` selected from the new Contacts-sourced
  //      dropdown (role === 'SUPPLIER'). Its `name` overrides the
  //      demo-data string.
  //   2. The order's pre-existing `supplier` field (legacy demo data).
  //      Real projects will have this empty / null in the future as
  //      Material Orders move off DEMO_ORDERS.
  // Email follows the same order: typed input wins, else selected
  // contact's email, else null. Pure deterministic logic — no AI.
  function resolveSupplierForOrder(order: MaterialOrder): { name: string | null; email: string | null } {
    const picked = supplierById.get(orderFrom[order.id] || '')
    const name = picked?.name?.trim() || order.supplier?.trim() || null
    const typedEmail = (supplierEmailById[order.id] || '').trim()
    const email = typedEmail || picked?.email?.trim() || null
    return { name, email }
  }

  const handleCreateSupplierThread = async (order: MaterialOrder) => {
    if (!projectId) return
    setThreadCreatingId(order.id)
    setThreadErrorById((prev) => ({ ...prev, [order.id]: null }))
    try {
      const supplier = resolveSupplierForOrder(order)
      // No email address — show an inline hint instead of creating a thread
      // with to:null that the builder must then fix inside the Emails module.
      if (!supplier.email) {
        setThreadErrorById((prev) => ({
          ...prev,
          [order.id]: isEs
            ? 'Selecciona un proveedor con email para crear el correo.'
            : 'Select a supplier with an email address first.',
        }))
        return
      }
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/material-orders/email-thread`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            materialOrderId: order.id,
            supplierName: supplier.name,
            supplierEmail: supplier.email,
            materialName: isEs ? (order.materialEs || order.material) : order.material,
            notesOrQuantity: isEs ? (order.notesEs || order.notes) : order.notes,
            onSiteDate: order.neededDate,
            orderByDate: order.orderByDate,
            language: isEs ? 'es' : 'en',
          }),
        },
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error || `HTTP ${res.status}`)
      }
      const data = await res.json()
      const threadId = data?.thread?.id
      if (!threadId) throw new Error('thread_id_missing')
      // Refresh the per-order summary so the row's badge + CTA
      // reflect the (possibly newly-created, possibly reused)
      // thread the next time the user lands back here.
      queryClient.invalidateQueries({ queryKey: ['material-orders-threads-summary', projectId] })
      // Hand off to Emails module — the thread + draft are
      // already persisted there. Use a project-scoped URL so
      // the user lands inside the same project context.
      navigate(`/projects/${projectId}/emails?thread=${encodeURIComponent(threadId)}`)
    } catch (err: any) {
      setThreadErrorById((prev) => ({
        ...prev,
        [order.id]:
          err?.message ||
          (isEs ? 'No se pudo crear el correo' : 'Could not create email'),
      }))
    } finally {
      setThreadCreatingId(null)
    }
  }

  const handleCreateOrder = async () => {
    if (!projectId || isDemo) return
    const material = createFormData.material.trim()
    const unit = createFormData.unit.trim()
    const quantity = parseFloat(createFormData.quantity)
    if (!material || !createFormData.category || !unit || isNaN(quantity) || quantity <= 0) {
      setCreateError(isEs ? 'Nombre, categoría, cantidad y unidad son requeridos.' : 'Item name, category, quantity, and unit are required.')
      return
    }
    setCreateError(null)
    setCreateLoading(true)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/material-orders`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          material,
          category: createFormData.category,
          quantity,
          unit,
          supplierContactId: createFormData.supplierContactId || undefined,
          neededByDate: createFormData.neededByDate || undefined,
          notes: createFormData.notes.trim() || undefined,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error || `HTTP ${res.status}`)
      }
      await queryClient.invalidateQueries({ queryKey: ['material-orders', projectId] })
      setShowCreateForm(false)
      setCreateFormData({ material: '', category: 'FRAMING', quantity: '', unit: '', supplierContactId: '', neededByDate: '', notes: '' })
    } catch (err: any) {
      setCreateError(err?.message || (isEs ? 'Error al crear orden' : 'Error creating order'))
    } finally {
      setCreateLoading(false)
    }
  }

  // PR #192: open edit modal pre-filled from the persisted DB row
  const handleOpenEdit = (orderId: string) => {
    if (isDemo) return
    const dbOrder = (dbOrders ?? []).find(o => o.id === orderId)
    if (!dbOrder) return
    setEditFormData({
      material: dbOrder.material,
      category: dbOrder.category,
      quantity: String(dbOrder.quantity),
      unit: dbOrder.unit,
      supplierContactId: dbOrder.supplierContactId ?? '',
      neededByDate: dbOrder.neededByDate ? dbOrder.neededByDate.slice(0, 10) : '',
      notes: dbOrder.notes ?? '',
    })
    setEditOrderId(orderId)
    setEditError(null)
    setShowEditForm(true)
  }

  // PR #192: PATCH the existing order and re-sync the query cache
  const handleEditOrder = async () => {
    if (!projectId || isDemo || !editOrderId) return
    const material = editFormData.material.trim()
    const unit = editFormData.unit.trim()
    const quantity = parseFloat(editFormData.quantity)
    if (!material || !editFormData.category || !unit || isNaN(quantity) || quantity <= 0) {
      setEditError(isEs ? 'Nombre, categoría, cantidad y unidad son requeridos.' : 'Item name, category, quantity, and unit are required.')
      return
    }
    setEditError(null)
    setEditLoading(true)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/material-orders/${editOrderId}`, {
        method: 'PATCH',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          material,
          category: editFormData.category,
          quantity,
          unit,
          supplierContactId: editFormData.supplierContactId || undefined,
          neededByDate: editFormData.neededByDate || undefined,
          notes: editFormData.notes.trim() || undefined,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error || `HTTP ${res.status}`)
      }
      await queryClient.invalidateQueries({ queryKey: ['material-orders', projectId] })
      setShowEditForm(false)
      setEditOrderId(null)
    } catch (err: any) {
      setEditError(err?.message || (isEs ? 'Error al editar orden' : 'Error editing order'))
    } finally {
      setEditLoading(false)
    }
  }

  // PR #193: manual status control — sends PATCH { status: newDbStatus } for real orders only.
  // Auto-advance (DRAFT → QUOTE_REQUESTED) is handled server-side when email-thread is created.
  // Demo is gated out entirely.
  const handleSetStatus = async (orderId: string, newStatus: string) => {
    if (isDemo || !projectId) return
    setStatusUpdatingId(orderId)
    try {
      const token = await getToken()
      await apiFetch(`/api/projects/${projectId}/material-orders/${orderId}`, {
        method: 'PATCH',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      })
      queryClient.invalidateQueries({ queryKey: ['material-orders', projectId] })
    } catch {
      /* best-effort — query cache will revert on next refetch */
    } finally {
      setStatusUpdatingId(null)
    }
  }

  // Filter
  // Phase 1 — `categoryFromQuery` adds an OPTIONAL category scope
  // on top of the existing status filter. When the user lands on
  // `/material-orders?category=FOUNDATION` from a Calendar reminder
  // link, only FOUNDATION rows render. The status filter still
  // composes normally. Both are read-only; no URL writes.
  const filtered = (filterStatus === 'all'
    ? orders
    : orders.filter(o => o.status === filterStatus)
  ).filter(o => !categoryFromQuery || o.category === categoryFromQuery)

  // Group by phase
  const phases = [...new Set(filtered.map(o => isEs ? o.phaseEs : o.phase))]

  // PR #193: updated stats counts for the 7 real statuses.
  // overdue/order-now removed — they were demo-only and always 0 for real projects.
  const draftCount = orders.filter(o => o.status === 'draft').length
  const quoteRequestedCount = orders.filter(o => o.status === 'quote-requested').length
  const quotedCount = orders.filter(o => o.status === 'quoted').length
  const orderedCount = orders.filter(o => o.status === 'ordered').length
  const deliveredCount = orders.filter(o => o.status === 'delivered').length
  const issueCount = orders.filter(o => o.status === 'issue').length
  const blockedCount = orders.filter(o => o.status === 'blocked').length
  const hasAnyEstimatedCost = orders.some(o => o.estimatedCost != null)
  const totalCost: number | null = hasAnyEstimatedCost
    ? orders.reduce((sum, o) => sum + (o.estimatedCost ?? 0), 0)
    : null

  // Mark as ordered
  const markOrdered = async (id: string) => {
    // For demo: update local state. For real: PATCH to API then refetch.
    if (isDemo) {
      setDemoOrders(prev => prev.map(o => o.id === id ? { ...o, status: 'ordered' as const } : o))
    } else {
      const token = await getToken()
      await apiFetch(`/api/projects/${projectId}/material-orders/${id}`, {
        method: 'PATCH',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'ORDERED' }),
      }).catch(() => {/* best-effort */})
      queryClient.invalidateQueries({ queryKey: ['material-orders', projectId] })
    }
  }

  // Export
  const exportCsv = () => {
    const rows = orders.map(o => [
      isEs ? o.phaseEs : o.phase,
      isEs ? o.materialEs : o.material,
      o.supplier, o.supplierPhone,
      o.orderByDate, o.neededDate,
      `${o.leadTimeDays}d`, `${o.bufferDays}d`,
      isEs ? STATUS_CONFIG[o.status].labelEs : STATUS_CONFIG[o.status].label,
      formatMoneyValue(o.estimatedCost, isEs),
    ])
    downloadCsv({
      filename: `material-order-schedule-${projectId}`,
      headers: [
        isEs ? 'Fase' : 'Phase', isEs ? 'Material' : 'Material',
        isEs ? 'Proveedor' : 'Supplier', isEs ? 'Teléfono' : 'Phone',
        isEs ? 'Ordenar antes de' : 'Order By', isEs ? 'Necesario en sitio' : 'Needed On Site',
        isEs ? 'Anticip.' : 'Lead Time', 'Buffer',
        'Status', isEs ? 'Costo estimado' : 'Estimated cost',
      ],
      rows,
    })
  }

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <Truck size={18} className="text-[#F59E0B]" />
            {isEs ? 'Programa de Pedidos de Material' : 'Material Ordering Schedule'}
          </h2>
          {/* Subtitle hidden for real projects with no orders yet — avoids
              showing "$0 total" which looks like a data error. */}
          {(isDemo || orders.length > 0) && (
            <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
              {isEs
                ? `${orders.length} materiales · Costo total estimado: ${formatMoneyValue(totalCost, isEs)}`
                : `${orders.length} materials · Total estimated: ${formatMoneyValue(totalCost, isEs)}`
              }
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* PR #191: only show the header New Order button when there are
              already orders. When the list is empty, the empty-state block
              below renders its own "Create first order" CTA — showing both
              simultaneously was confusing. Demo projects always have orders
              (DEMO_ORDERS has 21 entries) so this condition is naturally true
              for demo and demo behaviour is unchanged. */}
          {!isDemo && orders.length > 0 && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center gap-1.5 bg-[#06539B] text-white rounded-full py-1.5 px-4 text-[11px] font-[700] hover:bg-[#054a88]"
              data-testid="mo-header-create-btn"
            >
              <Plus size={12} /> {isEs ? 'Nueva orden' : 'New order'}
            </button>
          )}
          <button onClick={exportCsv}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[13px] font-[800] px-3 py-2 rounded-full hover:bg-[#F2F0EB]">
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      {/* PR #138B: demo banner. Demo users still see the schedule
          but no action buttons (mark-ordered + email-thread) render. */}
      {isDemo && (
        <SavedProjectOnlyNotice
          testId="material-orders-actions-demo-notice"
          bodyEn="Order tracking and supplier email actions are available in saved projects. Create or open a real project to mark orders and contact suppliers."
          bodyEs="El seguimiento de pedidos y las acciones de correo a proveedores están disponibles en proyectos guardados. Crea o abre un proyecto real para marcar pedidos y contactar proveedores."
        />
      )}

      {/* Financial Trust P0 (PR-F5): cost tracking limitation + not-connected honesty note */}
      <div
        data-testid="mo-cost-trust-note"
        className="flex items-start gap-2 rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2.5"
      >
        <Info size={13} className="text-[#9E9890] mt-0.5 shrink-0" />
        <p className="text-[10px] font-[600] text-[#6B6660] leading-relaxed">
          {isEs
            ? 'Los costos estimados son solo de referencia para planificación. Para proyectos reales, los costos de materiales se registran en Cotizaciones y Contratos. Las órdenes de material aún no están conectadas al Presupuesto.'
            : 'Estimated costs are for planning reference only. For real projects, material costs are tracked through Quotes and Contracts. Material orders are not yet connected to the Budget.'}
        </p>
      </div>

      {/* Phase 1 — category scope pill. Renders only when the URL
          carries `?category=…`. Lets the user clear the scope back
          to the full schedule. Clicking "clear" navigates to the
          same route without the query — no auto-actions, no
          purchase semantics, just a navigation context echo. */}
      {categoryFromQuery && (
        <div
          className="flex items-center justify-between gap-2 rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2"
          data-testid="material-orders-category-scope-pill"
        >
          <div className="text-[11px] font-[700] text-[#1C1A17]">
            {isEs
              ? `Mostrando solo: ${categoryFromQuery}`
              : `Showing only: ${categoryFromQuery}`}
          </div>
          <button
            type="button"
            onClick={() => projectId && navigate(`/projects/${projectId}/material-orders`)}
            className="text-[10px] font-[800] text-[#06539B] hover:underline"
          >
            {isEs ? 'Mostrar todas las categorías' : 'Show all categories'}
          </button>
        </div>
      )}

      {/* PR-183: honest empty state for real projects with no material orders yet.
          Rendered instead of the stats / filter / orders list when the project
          is real (not a demo) and orders array is empty. Demo projects continue
          to show DEMO_ORDERS as before. */}
      {!isDemo && orders.length === 0 && (
        <div
          className="bg-white border border-[#ECEAE4] rounded-2xl p-8 text-center space-y-3"
          data-testid="material-orders-empty-state"
        >
          <div className="text-[40px] leading-none" role="img" aria-label={isEs ? 'Caja' : 'Box'}>📦</div>
          <div className="text-[15px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
            {isEs
              ? 'Aún no hay pedidos de material.'
              : 'No material orders yet.'}
          </div>
          <div className="text-[12px] font-[600] text-[#9E9890] max-w-xs mx-auto leading-relaxed">
            {isEs
              ? 'Tu programa de materiales aparecerá aquí cuando las cantidades sean aprobadas o agregadas.'
              : 'Your material schedule will appear here once quantities are approved or added.'}
          </div>
          <p className="text-[11px] font-[500] text-[#CECAC4]">
            {isEs
              ? 'Por ahora, usa este módulo para gestionar pedidos de material preliminares cuando existan.'
              : 'For now, use this module to manage draft material orders once they exist.'}
          </p>
          {!isDemo && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-4 inline-flex items-center gap-2 bg-[#06539B] text-white rounded-full py-2 px-5 text-[12px] font-[700] hover:bg-[#054a88]"
              data-testid="mo-empty-create-btn"
            >
              <Plus size={14} /> {isEs ? 'Crear primera orden' : 'Create first order'}
            </button>
          )}
        </div>
      )}

      {/* Stats row, filters, and orders list — only rendered when there are orders.
          Applies to both demo projects (DEMO_ORDERS) and future real-data projects. */}
      {orders.length > 0 && (<>

      {/* PR #193: urgent alert for orders with issues or blocked. Replaces the old
          overdue/order-now banner which was always 0 for real projects. */}
      {(issueCount > 0 || blockedCount > 0) && (
        <div className={`flex items-center gap-3 rounded-xl p-3 ${
          blockedCount > 0 ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
        }`} data-testid="mo-urgent-alert">
          <AlertTriangle size={18} className={blockedCount > 0 ? 'text-red-500' : 'text-amber-500'} />
          <div className="flex-1">
            <div className={`text-[12px] font-[800] ${blockedCount > 0 ? 'text-red-700' : 'text-amber-700'}`}>
              {blockedCount > 0
                ? (isEs ? `${blockedCount} pedido(s) bloqueado(s)` : `${blockedCount} order(s) blocked`)
                : (isEs ? `${issueCount} pedido(s) con problema(s)` : `${issueCount} order(s) with issues`)
              }
            </div>
            <div className={`text-[10px] font-[600] ${blockedCount > 0 ? 'text-red-600' : 'text-amber-600'}`}>
              {isEs
                ? 'Revisa los pedidos marcados para resolver el problema.'
                : 'Review flagged orders to resolve the issue.'
              }
            </div>
          </div>
        </div>
      )}

      {/* Stats row — F3: 2-col on mobile (4 tiles wrap to 2x2 at
          <md), 4-col on tablet/desktop (≥md). Same 4 status tiles,
          same content, same click targets — only the column count
          adapts so each tile stays readable at narrow widths. */}
      {/* PR #193: tiles updated to reflect the real 7-status workflow.
          Order Now / Upcoming replaced with Draft / Quoted — these are
          meaningful for real projects (not always-zero like the old tiles). */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="mo-stats-tiles">
        {[
          { label: isEs ? 'Borrador' : 'Draft', count: draftCount + quoteRequestedCount, color: '#9E9890', bg: 'bg-[#F2F0EB]' },
          { label: isEs ? 'Cotizado' : 'Quoted', count: quotedCount, color: '#7C3AED', bg: 'bg-purple-50' },
          { label: isEs ? 'Ordenados' : 'Ordered', count: orderedCount, color: '#00BCD4', bg: 'bg-cyan-50' },
          { label: isEs ? 'Entregados' : 'Delivered', count: deliveredCount, color: '#22C55E', bg: 'bg-green-50' },
        ].map(stat => (
          <div key={stat.label} className={`${stat.bg} rounded-xl p-3 text-center`}>
            <div className="text-[20px] font-[900]" style={{ color: stat.color }}>{stat.count}</div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* PR #193: filter tabs updated to match the 7 real statuses.
          Overdue / Order Now removed — they were demo-only and always 0 for real projects. */}
      <div className="flex gap-2 overflow-x-auto" data-testid="mo-filter-tabs">
        {[
          { key: 'all', label: isEs ? 'Todos' : 'All' },
          { key: 'draft', label: isEs ? 'Borrador' : 'Draft' },
          { key: 'quote-requested', label: isEs ? 'Cotización Sol.' : 'Quote Req.' },
          { key: 'quoted', label: isEs ? 'Cotizado' : 'Quoted' },
          { key: 'ordered', label: isEs ? 'Ordenados' : 'Ordered' },
          { key: 'delivered', label: isEs ? 'Entregados' : 'Delivered' },
          { key: 'issue', label: isEs ? 'Problema' : 'Issue' },
          { key: 'blocked', label: isEs ? 'Bloqueado' : 'Blocked' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilterStatus(f.key)}
            className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
              filterStatus === f.key
                ? 'bg-[#06539B] text-white border-[#06539B]'
                : 'bg-white border-[#ECEAE4] text-[#9E9890] hover:border-[#06539B]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Orders grouped by phase */}
      <div className="space-y-4">
        {phases.map(phase => {
          const phaseOrders = filtered.filter(o => (isEs ? o.phaseEs : o.phase) === phase)
          const phaseHasEstimatedCost = phaseOrders.some(o => o.estimatedCost != null)
          const phaseTotal: number | null = phaseHasEstimatedCost
            ? phaseOrders.reduce((s, o) => s + (o.estimatedCost ?? 0), 0)
            : null
          return (
            <div key={phase}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[12px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">{phase}</h3>
                <span className="text-[11px] font-[800] text-[#1C1A17]">{formatMoneyValue(phaseTotal, isEs)}</span>
              </div>
              <div className="space-y-2">
                {phaseOrders.map(order => {
                  const config = STATUS_CONFIG[order.status]
                  const isExpanded = expandedId === order.id
                  const summaryRow = threadsSummary?.[order.id]
                  const emailKind = kindOf(summaryRow)
                  const emailBadgeText = emailBadgeLabel(emailKind)
                  // Phase 2 — supplier-selection return position.
                  // Each card carries a stable DOM id + data-order-id
                  // so the consuming effect can scroll-into-view and
                  // briefly highlight the targeted card after a
                  // contact pick handoff from ContactsModule. Both
                  // hooks are off by default for all other paths
                  // (only the supplier-selection effect targets them).
                  const isHighlightedFromSelection = highlightedOrderId === order.id
                  return (
                    <div
                      key={order.id}
                      id={`material-order-${order.id}`}
                      data-order-id={order.id}
                      className={`bg-white border rounded-xl overflow-hidden transition-all ${isHighlightedFromSelection ? 'border-emerald-400 ring-2 ring-emerald-200' : config.border}`}
                    >
                      {/* Main row */}
                      <div
                        onClick={() => setExpandedId(isExpanded ? null : order.id)}
                        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#FAFAF8]"
                      >
                        {/* Status icon */}
                        <div className={`w-[32px] h-[32px] rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                          <config.icon size={14} style={{ color: config.color }} />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-[800] text-[#1C1A17] truncate">
                            {isEs ? order.materialEs : order.material}
                          </div>
                          <div className="text-[10px] font-[600] text-[#9E9890] mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>{order.supplier}</span>
                            <span>·</span>
                            <span className="flex items-center gap-0.5">
                              <Clock size={9} /> {order.leadTimeDays}d {isEs ? 'anticip.' : 'lead'}
                            </span>
                          </div>
                          {emailKind !== 'NONE' && (
                            // Full-phrase email-status pill on its own
                            // line. The previous single-word badge in the
                            // supplier line was too easy to miss — users
                            // didn't realize the email had been sent.
                            <div
                              className={`inline-flex items-center gap-1.5 mt-1.5 text-[10px] font-[800] px-2.5 py-1 rounded-full ${
                                emailKind === 'REPLIED'
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                  : emailKind === 'SENT'
                                  ? 'bg-[#06539B]/10 text-[#06539B] border border-[#06539B]/30'
                                  : emailKind === 'CLOSED'
                                  ? 'bg-[#F2F0EB] text-[#9E9890] border border-[#ECEAE4]'
                                  : 'bg-amber-100 text-amber-800 border border-amber-200'
                              }`}
                              aria-label={isEs ? `Estado del correo: ${emailBadgeText}` : `Email status: ${emailBadgeText}`}
                            >
                              <MessageSquare size={11} />
                              {emailBadgeText}
                            </div>
                          )}
                        </div>

                        {/* Order-by date + status */}
                        <div className="text-right flex-shrink-0">
                          <div className="text-[11px] font-[800] text-[#1C1A17]">
                            {order.orderByDate
                              ? `${isEs ? 'Ordenar antes de:' : 'Order by:'} ${order.orderByDate}`
                              : ''}
                          </div>
                          <span className={`text-[8px] font-[800] px-2 py-0.5 rounded-full uppercase ${config.bg}`}
                            style={{ color: config.color }}>
                            {isEs ? config.labelEs : config.label}
                          </span>
                        </div>

                        <ChevronDown size={14} className={`text-[#9E9890] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-[#F2F0EB] pt-3 space-y-3">
                          {/* PR-NEW: expanded email-state panel.
                              When the row has an associated thread,
                              render a localized, visually distinct
                              banner so the user immediately knows
                              the conversation state and (for SENT)
                              when it went out. */}
                          {emailKind !== 'NONE' && summaryRow && (
                            <div
                              className={`rounded-lg px-3 py-2 border ${
                                emailKind === 'REPLIED'
                                  ? 'bg-emerald-50 border-emerald-200'
                                  : emailKind === 'SENT'
                                  ? 'bg-[#06539B]/5 border-[#06539B]/20'
                                  : emailKind === 'CLOSED'
                                  ? 'bg-[#FAFAF8] border-[#ECEAE4]'
                                  : 'bg-amber-50 border-amber-200'
                              }`}
                            >
                              <div className="flex items-center gap-2 flex-wrap">
                                <MessageSquare
                                  size={12}
                                  className={
                                    emailKind === 'REPLIED'
                                      ? 'text-emerald-700'
                                      : emailKind === 'SENT'
                                      ? 'text-[#06539B]'
                                      : emailKind === 'CLOSED'
                                      ? 'text-[#9E9890]'
                                      : 'text-amber-700'
                                  }
                                />
                                <span
                                  className={`text-[11px] font-[800] ${
                                    emailKind === 'REPLIED'
                                      ? 'text-emerald-800'
                                      : emailKind === 'SENT'
                                      ? 'text-[#06539B]'
                                      : emailKind === 'CLOSED'
                                      ? 'text-[#9E9890]'
                                      : 'text-amber-800'
                                  }`}
                                >
                                  {emailExpandedHeadline(emailKind)}
                                </span>
                              </div>
                              {emailKind === 'SENT' && summaryRow.sentAt && (() => {
                                const d = new Date(summaryRow.sentAt)
                                const stamp = Number.isNaN(d.getTime())
                                  ? ''
                                  : d.toLocaleString(isEs ? 'es-MX' : 'en-US')
                                if (!stamp) return null
                                return (
                                  <div className="text-[10px] font-[600] text-[#06539B] mt-0.5">
                                    {isEs ? `Enviado el ${stamp}` : `Sent on ${stamp}`}
                                  </div>
                                )
                              })()}
                              {emailKind === 'REPLIED' && summaryRow.lastInboundAt && (() => {
                                const d = new Date(summaryRow.lastInboundAt)
                                const stamp = Number.isNaN(d.getTime())
                                  ? ''
                                  : d.toLocaleString(isEs ? 'es-MX' : 'en-US')
                                if (!stamp) return null
                                return (
                                  <div className="text-[10px] font-[600] text-emerald-700 mt-0.5">
                                    {isEs ? `Respuesta recibida el ${stamp}` : `Reply received on ${stamp}`}
                                  </div>
                                )
                              })()}
                            </div>
                          )}
                          {/* Timeline visual */}
                          <div className="flex items-center gap-2 text-[10px]">
                            <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-full">
                              <ShoppingCart size={10} className="text-amber-500" />
                              <span className="font-[700] text-amber-700">{isEs ? 'Ordenar' : 'Order'}: {order.orderByDate}</span>
                            </div>
                            <ArrowRight size={12} className="text-[#ECEAE4]" />
                            <div className="flex items-center gap-1 text-[#9E9890]">
                              <Clock size={10} />
                              <span className="font-[600]">{order.leadTimeDays} {isEs ? 'días' : 'days'} + {order.bufferDays}d buffer</span>
                            </div>
                            <ArrowRight size={12} className="text-[#ECEAE4]" />
                            <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full">
                              <Calendar size={10} className="text-green-500" />
                              <span className="font-[700] text-green-700">{isEs ? 'En sitio' : 'On site'}: {order.neededDate}</span>
                            </div>
                          </div>

                          {/* Supplier + cost — F3: stacks vertically
                              on mobile (<md) so the tappable `tel:`
                              phone link doesn't truncate; goes 3-col
                              from md+ for desktop parity. Same three
                              fields, same supplier flow (PR #155), no
                              behavioral change. */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <div className="text-[9px] font-[700] text-[#9E9890] uppercase">{isEs ? 'Proveedor' : 'Supplier'}</div>
                              <div className="text-[11px] font-[700] text-[#1C1A17] mt-0.5">{order.supplier}</div>
                            </div>
                            <div>
                              <div className="text-[9px] font-[700] text-[#9E9890] uppercase">{isEs ? 'Teléfono' : 'Phone'}</div>
                              <a href={`tel:${order.supplierPhone}`} className="text-[11px] font-[700] text-[#06539B] mt-0.5 flex items-center gap-1">
                                <Phone size={10} /> {order.supplierPhone}
                              </a>
                            </div>
                            <div>
                              <div className="text-[9px] font-[700] text-[#9E9890] uppercase">{isEs ? 'Costo estimado' : 'Estimated cost'}</div>
                              <div className="text-[14px] font-[900] text-[#1C1A17] mt-0.5">{formatMoneyValue(order.estimatedCost, isEs)}</div>
                            </div>
                          </div>

                          {/* Notes */}
                          {(order.notes || order.notesEs) && (
                            <div className="bg-[#FAFAF8] rounded-lg p-2.5">
                              <div className="text-[9px] font-[700] text-[#9E9890] uppercase mb-1">
                                {isEs ? 'Notas' : 'Notes'}
                              </div>
                              <div className="text-[11px] font-[600] text-[#1C1A17]">
                                {isEs ? order.notesEs || order.notes : order.notes}
                              </div>
                            </div>
                          )}

                          {/* Order from dropdown — Contacts-sourced.
                              Hardcoded Home Depot / Lowe's / 84 Lumber /
                              "Local Lumber Yard" / "Other" options were
                              removed: real procurement happens with the
                              builder's own suppliers, which live as
                              `ProjectContact` rows with role === 'SUPPLIER'.
                              When no supplier contacts exist we show a
                              bilingual empty state with an "Add supplier"
                              CTA that deep-links into the Contacts
                              module. */}
                          {pickedFromContactsForOrderId === order.id && (
                            <div
                              data-testid={`mo-supplier-picked-confirm-${order.id}`}
                              className="flex items-start justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 mb-2"
                            >
                              <p className="text-[11px] font-[700] text-emerald-800 leading-snug">
                                {isEs
                                  ? 'Proveedor seleccionado desde Contactos. Revisa el pedido antes de enviar.'
                                  : 'Supplier selected from Contacts. Review the order before sending.'}
                              </p>
                              <button
                                type="button"
                                onClick={() => setPickedFromContactsForOrderId(null)}
                                aria-label={isEs ? 'Cerrar' : 'Dismiss'}
                                className="text-[10px] font-[800] text-emerald-700 hover:text-emerald-900"
                              >
                                ×
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <div className="text-[9px] font-[700] text-[#9E9890] uppercase mb-1">{isEs ? 'Ordenar de' : 'Order from'}</div>
                              {supplierContacts.length === 0 ? (
                                <div className="border border-dashed border-[#ECEAE4] rounded-lg px-3 py-2 bg-white">
                                  <p className="text-[11px] font-[700] text-[#6B6660] leading-snug">
                                    {isEs
                                      ? 'No se encontraron proveedores. Agrega un proveedor en Contactos para usarlo en Órdenes de Materiales.'
                                      : 'No suppliers found. Add a supplier in Contacts to use it in Material Orders.'}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!projectId) return
                                      // Phase 1 — preserve return context. Contacts
                                      // page reads `from=material-orders` and renders
                                      // a back pill. Category (if any) is echoed so
                                      // the back link can re-scope the schedule. The
                                      // originating `orderId` is now included so the
                                      // "Use this supplier" button in Contacts can
                                      // hand the contact back to THIS specific order.
                                      // No supplier names/emails travel in the URL.
                                      const params = new URLSearchParams({ from: 'material-orders', orderId: order.id })
                                      if (categoryFromQuery) params.set('category', categoryFromQuery)
                                      navigate(`/projects/${projectId}/contacts?${params.toString()}`)
                                    }}
                                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-[800] text-[#06539B] hover:underline"
                                    aria-label={isEs ? 'Agregar proveedor en Contactos' : 'Add supplier in Contacts'}
                                  >
                                    <Plus size={12} /> {isEs ? 'Agregar proveedor' : 'Add supplier'}
                                  </button>
                                </div>
                              ) : (
                                <select
                                  value={orderFrom[order.id] || ''}
                                  onChange={e => {
                                    const contactId = e.target.value
                                    setOrderFrom(prev => ({ ...prev, [order.id]: contactId }))
                                    // When a supplier with a known email is
                                    // selected, auto-fill the supplier-email
                                    // input. We only auto-fill when the user
                                    // hasn't manually typed something into the
                                    // field already (string presence check) so
                                    // we never overwrite a manual edit.
                                    const picked = supplierById.get(contactId)
                                    const existingTyped = (supplierEmailById[order.id] || '').trim()
                                    if (picked?.email && !existingTyped) {
                                      setSupplierEmailById(prev => ({ ...prev, [order.id]: picked.email! }))
                                    }
                                    // Dropdown path — persist to DB (same PATCH shape as the
                                    // Contacts handoff useEffect above). Best-effort; non-fatal.
                                    if (!isDemo && projectId && order.id && contactId) {
                                      getToken().then(token => {
                                        apiFetch(`/api/projects/${projectId}/material-orders/${order.id}`, {
                                          method: 'PATCH',
                                          headers: {
                                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                            'Content-Type': 'application/json',
                                          },
                                          body: JSON.stringify({ supplierContactId: contactId }),
                                        }).then(() => {
                                          queryClient.invalidateQueries({ queryKey: ['material-orders', projectId] })
                                        }).catch(() => {/* best-effort */})
                                      })
                                    }
                                  }}
                                  className="w-full border border-[#ECEAE4] rounded-lg px-2 py-1.5 text-[11px] font-[700] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                                  aria-label={isEs ? 'Seleccionar proveedor' : 'Select supplier'}
                                  data-testid="mo-order-from-select"
                                >
                                  <option value="">
                                    {isEs ? 'Selecciona un proveedor…' : 'Select a supplier…'}
                                  </option>
                                  {supplierContacts.map(c => (
                                    <option key={c.id} value={c.id}>
                                      {buildSupplierLabel(c)}{c.email ? '' : isEs ? ' — (sin email)' : ' — (no email)'}
                                    </option>
                                  ))}
                                </select>
                              )}
                              {/* Order-scoped Contacts CTA — always rendered.
                                  PR #167 added an "Add supplier" link only
                                  when supplierContacts.length === 0 (the
                                  empty-state). Once any supplier contact
                                  existed, the only way into Contacts from
                                  here was the sidebar — which doesn't echo
                                  `orderId` (PR #174's gate only emits
                                  `?from=material-orders`), so the "Use this
                                  supplier" handoff button on Contacts didn't
                                  render and the user couldn't round-trip back
                                  to this specific order. Rendering this CTA
                                  unconditionally restores the order-scoped
                                  Path A handoff: clicking it navigates to
                                  /contacts?from=material-orders&orderId=<id>
                                  [&category=…], which is the same URL shape
                                  the empty-state button already produces.
                                  No additional state, no backend, no email/
                                  thread side effects — pure URL construction
                                  + navigate(). */}
                              <button
                                type="button"
                                onClick={() => {
                                  if (!projectId) return
                                  const params = new URLSearchParams({ from: 'material-orders', orderId: order.id })
                                  if (categoryFromQuery) params.set('category', categoryFromQuery)
                                  navigate(`/projects/${projectId}/contacts?${params.toString()}`)
                                }}
                                data-testid={`mo-order-scoped-contacts-cta-${order.id}`}
                                className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-[700] text-[#06539B] hover:underline"
                                aria-label={isEs ? 'Agregar o elegir proveedor en Contactos' : 'Add or choose supplier in Contacts'}
                              >
                                <Plus size={11} /> {isEs ? 'Agregar o elegir proveedor en Contactos' : 'Add or choose supplier in Contacts'}
                              </button>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* PR #193: Mark as ordered available for draft, quote-requested, quoted.
                                These are the pre-order statuses where the builder hasn't placed a PO yet. */}
                            {!isDemo && (order.status === 'draft' || order.status === 'quote-requested' || order.status === 'quoted') && (
                              <button
                                onClick={() => markOrdered(order.id)}
                                data-testid="mo-mark-ordered-btn"
                                className="flex items-center gap-1.5 bg-[#22C55E] text-white text-[13px] font-[800] px-4 py-2 rounded-full hover:bg-[#16A34A]"
                              >
                                <Check size={12} /> {isEs ? 'Marcar como ordenado' : 'Mark as ordered'}
                              </button>
                            )}
                            {/* PR-NEW: status-aware email CTA.
                                When no thread exists for this order
                                yet, opens the inline draft form.
                                When a thread exists (DRAFT / SENT /
                                REPLIED / CLOSED), deep-links into
                                the Emails module so the user picks
                                up the conversation where it left
                                off — no duplicate conversations. */}
                            {emailKind === 'NONE' ? (
                              // PR #138B: the create-email affordance opens
                              // a draft that would POST `/material-orders/
                              // email-thread`. Hidden in demo so no thread
                              // can be created against a non-existent
                              // project + non-existent supplier email.
                              !isDemo && (
                                <button
                                  onClick={() => handleCreateSupplierThread(order)}
                                  disabled={threadCreatingId === order.id}
                                  className="flex items-center gap-1.5 border border-[#06539B] text-[#06539B] text-[13px] font-[800] px-3 py-2 rounded-full hover:bg-[#06539B]/5 disabled:opacity-60"
                                  aria-label={isEs ? 'Crear correo en NATAMI' : 'Create email in NATAMI'}
                                >
                                  <Mail size={12} />
                                  {threadCreatingId === order.id
                                    ? (isEs ? 'Creando…' : 'Creating…')
                                    : emailCtaLabel('NONE')}
                                </button>
                              )
                            ) : (
                              <button
                                onClick={() => summaryRow && openThread(summaryRow.threadId)}
                                className={`flex items-center gap-1.5 text-[13px] font-[800] px-3 py-2 rounded-full ${
                                  emailKind === 'REPLIED'
                                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                    : emailKind === 'SENT'
                                    ? 'bg-[#06539B] text-white hover:bg-[#14457E]'
                                    : 'border border-[#06539B] text-[#06539B] hover:bg-[#06539B]/5'
                                }`}
                                aria-label={emailCtaLabel(emailKind)}
                              >
                                <MessageSquare size={12} />
                                {emailCtaLabel(emailKind)}
                              </button>
                            )}
                            {emailKind === 'SENT' && summaryRow && !summaryRow.lastInboundAt && (
                              <span className="text-[10px] font-[700] text-[#9E9890] italic">
                                {isEs ? 'Esperando respuesta del proveedor…' : 'Waiting for supplier reply…'}
                              </span>
                            )}
                            {/* PR-NEW: WhatsApp removed from the
                                Material Orders supplier workflow.
                                Material suppliers/vendors generally use
                                email and phone; WhatsApp lived here as
                                a leftover from the old contractor flow.
                                WhatsApp stays available elsewhere (e.g.
                                Contacts, contractor/subcontractor
                                screens) — this only hides it from this
                                module. */}
                            <a href={`tel:${order.supplierPhone}`}
                              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#9E9890] text-[13px] font-[800] px-3 py-2 rounded-full hover:bg-[#F2F0EB]">
                              <Phone size={12} /> {isEs ? 'Llamar' : 'Call'}
                            </a>
                            {/* PR #192: edit order — real projects only */}
                            {!isDemo && (
                              <button
                                onClick={() => handleOpenEdit(order.id)}
                                className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#9E9890] text-[13px] font-[800] px-3 py-2 rounded-full hover:bg-[#F2F0EB]"
                                data-testid="mo-edit-btn"
                                aria-label={isEs ? 'Editar orden' : 'Edit order'}
                              >
                                <Pencil size={12} /> {isEs ? 'Editar' : 'Edit'}
                              </button>
                            )}
                          </div>

                          {/* PR #193: manual status control — real projects only.
                              Demo remains read-only. The dropdown sends a PATCH
                              { status: dbValue } via handleSetStatus. Only the
                              7 supported statuses are offered; CANCELLED is not
                              a builder-facing action. */}
                          {!isDemo && (
                            <div className="flex items-center gap-2 pt-1" data-testid="mo-status-control">
                              <label className="text-[9px] font-[700] text-[#9E9890] uppercase shrink-0">
                                {isEs ? 'Estado:' : 'Status:'}
                              </label>
                              <select
                                value={order.statusDb || 'DRAFT'}
                                onChange={e => handleSetStatus(order.id, e.target.value)}
                                disabled={statusUpdatingId === order.id}
                                data-testid="mo-status-select"
                                aria-label={isEs ? 'Cambiar estado de la orden' : 'Change order status'}
                                className="border border-[#ECEAE4] rounded-lg px-2 py-1 text-[11px] font-[700] text-[#1C1A17] focus:outline-none focus:border-[#06539B] disabled:opacity-60"
                              >
                                <option value="DRAFT">{isEs ? 'Borrador' : 'Draft'}</option>
                                <option value="QUOTE_REQUESTED">{isEs ? 'Cotización solicitada' : 'Quote Requested'}</option>
                                <option value="QUOTED">{isEs ? 'Cotizado' : 'Quoted'}</option>
                                <option value="ORDERED">{isEs ? 'Ordenado' : 'Ordered'}</option>
                                <option value="DELIVERED">{isEs ? 'Entregado' : 'Delivered'}</option>
                                <option value="ISSUE">{isEs ? 'Problema' : 'Issue'}</option>
                                <option value="BLOCKED">{isEs ? 'Bloqueado' : 'Blocked'}</option>
                              </select>
                              {statusUpdatingId === order.id && (
                                <span className="text-[10px] font-[600] text-[#9E9890]">
                                  {isEs ? 'Guardando…' : 'Saving…'}
                                </span>
                              )}
                            </div>
                          )}

                          {/* PR-NEW: inline supplier-email form +
                              local bilingual draft. No backend
                              call; no AI; the draft is generated
                              by `buildSupplierEmailDraft` from
                              already-known data and opened in
                              the user's mail client via mailto:. */}
                          {emailFormOpenId === order.id && (() => {
                            const supplierEmailRaw = supplierEmailById[order.id] || ''
                            const supplierEmail = supplierEmailRaw.trim()
                            const hasSupplierEmail = supplierEmail.length > 0
                            const currentDraft = draftByOrderId[order.id] || null
                            const toast = draftToastByOrderId[order.id] || null
                            return (
                              <div className="border border-[#ECEAE4] rounded-lg p-3 space-y-3 bg-[#FAFAF8]">
                                <div className="flex items-center gap-2">
                                  <Mail size={14} className="text-[#06539B]" />
                                  <span className="text-[12px] font-[800] text-[#1C1A17]">
                                    {isEs ? 'Crear correo al proveedor' : 'Create supplier email'}
                                  </span>
                                </div>

                                {/* Reminder bar — bilingual order-by / needed-on-site dates. */}
                                <div className="grid grid-cols-2 gap-2 text-[10px] font-[700] text-[#9E9890]">
                                  <div>
                                    {isEs ? 'Ordenar antes de:' : 'Order by:'}{' '}
                                    <span className="font-[800] text-[#1C1A17]">{order.orderByDate || '—'}</span>
                                  </div>
                                  <div>
                                    {isEs ? 'Requerido en obra:' : 'Needed on site:'}{' '}
                                    <span className="font-[800] text-[#1C1A17]">{order.neededDate || '—'}</span>
                                  </div>
                                </div>

                                {/* Supplier email input — bilingual label + placeholder. */}
                                <div>
                                  <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
                                    {isEs ? 'Email del proveedor' : 'Supplier email'}
                                  </label>
                                  <input
                                    type="email"
                                    value={supplierEmailRaw}
                                    onChange={(e) => setSupplierEmailById((prev) => ({ ...prev, [order.id]: e.target.value }))}
                                    placeholder={isEs ? 'proveedor@ejemplo.com' : 'supplier@example.com'}
                                    className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
                                  />
                                  {!hasSupplierEmail && (
                                    <p className="text-[10px] font-[700] text-amber-600 mt-1">
                                      {isEs
                                        ? 'Agrega el email del proveedor antes de enviar'
                                        : 'Add supplier email before sending'}
                                    </p>
                                  )}
                                </div>

                                {/* Generate draft button — pure FE, no AI. */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const resolved = resolveSupplierForOrder(order)
                                    const draft = buildSupplierEmailDraft({
                                      projectName,
                                      supplierName: resolved.name,
                                      materialName: isEs ? (order.materialEs || order.material) : order.material,
                                      notesOrQuantity: isEs ? (order.notesEs || order.notes) : order.notes,
                                      onSiteDate: order.neededDate,
                                      orderByDate: order.orderByDate,
                                    }, isEs)
                                    setDraftByOrderId((prev) => ({ ...prev, [order.id]: draft }))
                                    setDraftToastByOrderId((prev) => ({
                                      ...prev,
                                      [order.id]: isEs ? 'Borrador creado' : 'Draft created',
                                    }))
                                    setTimeout(() => {
                                      setDraftToastByOrderId((prev) => ({ ...prev, [order.id]: null }))
                                    }, 3000)
                                  }}
                                  className="text-[11px] font-[800] border border-[#06539B] text-[#06539B] rounded-full px-3 py-1.5 hover:bg-[#06539B]/5"
                                >
                                  {isEs ? 'Crear correo al proveedor' : 'Create supplier email'}
                                </button>

                                {toast && (
                                  <div className="text-[10px] font-[700] text-emerald-600">{toast}</div>
                                )}

                                {/* Draft preview + mailto launcher. */}
                                {currentDraft && (
                                  <div className="border border-[#ECEAE4] rounded-lg p-2.5 bg-white space-y-2">
                                    <div>
                                      <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
                                        {isEs ? 'Asunto' : 'Subject'}
                                      </div>
                                      <div className="text-[11px] font-[700] text-[#1C1A17]">{currentDraft.subject}</div>
                                    </div>
                                    <div>
                                      <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
                                        {isEs ? 'Mensaje' : 'Body'}
                                      </div>
                                      <pre className="text-[11px] font-[600] text-[#1C1A17] whitespace-pre-wrap font-sans">
                                        {currentDraft.body}
                                      </pre>
                                    </div>
                                    <p className="text-[10px] font-[600] text-[#9E9890] italic">
                                      {isEs ? 'Revisa el correo antes de enviarlo' : 'Review email before sending'}
                                    </p>
                                    {/* PR-NEW: primary path. Creates a
                                        persistent EmailThread + DRAFT
                                        EmailMessage and navigates to the
                                        Emails module for review + send.
                                        Reply-To uses a per-thread inbound
                                        address so the recipient's reply
                                        lands inside NATAMI Correos. */}
                                    <div className="flex flex-wrap items-center gap-2">
                                      {/* PR #138B: the inline draft form is
                                          gated by !isDemo at the trigger above
                                          (form cannot open in demo). The
                                          `disabled={isDemo || …}` here is
                                          belt-and-suspenders. The onClick
                                          shape stays as it was so the existing
                                          emails-material-orders smoke test
                                          continues to recognize the primary
                                          CTA wiring. */}
                                      <button
                                        type="button"
                                        onClick={() => handleCreateSupplierThread(order)}
                                        disabled={isDemo || threadCreatingId === order.id}
                                        className="inline-flex items-center gap-1.5 text-[11px] font-[800] rounded-full px-3 py-1.5 bg-[#06539B] text-white hover:bg-[#14457E] disabled:opacity-60"
                                      >
                                        <Mail size={11} />
                                        {threadCreatingId === order.id
                                          ? (isEs ? 'Creando…' : 'Creating…')
                                          : (isEs ? 'Crear correo en NATAMI' : 'Create email in NATAMI')}
                                      </button>
                                      {/* PR-NEW: graceful fallback only.
                                          If the create-thread call
                                          failed (offline / 5xx), the
                                          user can still pop their
                                          local mail client. The button
                                          is rendered as a less-prominent
                                          secondary action so the
                                          primary path stays the
                                          tracked thread. */}
                                      <a
                                        href={
                                          hasSupplierEmail
                                            ? `mailto:${supplierEmail}?subject=${encodeURIComponent(currentDraft.subject)}&body=${encodeURIComponent(currentDraft.body)}`
                                            : undefined
                                        }
                                        aria-disabled={!hasSupplierEmail}
                                        onClick={(e) => { if (!hasSupplierEmail) e.preventDefault() }}
                                        title={isEs ? 'Abrir en cliente de correo local' : 'Open in local mail client'}
                                        className={`inline-flex items-center gap-1.5 text-[10px] font-[700] rounded-full px-2.5 py-1 border ${
                                          hasSupplierEmail
                                            ? 'border-[#ECEAE4] text-[#9E9890] hover:bg-[#FAFAF8]'
                                            : 'border-[#ECEAE4] text-[#CECAC4] cursor-not-allowed'
                                        }`}
                                      >
                                        {isEs ? 'Abrir en cliente de correo' : 'Open in mail client'}
                                      </a>
                                    </div>
                                    {threadErrorById[order.id] && (
                                      <p className="text-[10px] font-[700] text-red-600">
                                        {threadErrorById[order.id]}
                                      </p>
                                    )}
                                  </div>
                                )}

                                {/* Confirmation footer reiterating the SLA fields the draft asks for. */}
                                <p className="text-[10px] font-[600] text-[#9E9890]">
                                  {isEs
                                    ? 'Disponibilidad, precio y tiempo de entrega'
                                    : 'Availability, price, and delivery time'}
                                </p>
                              </div>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      </>)}

      {/* Total bar — hidden when no orders to avoid showing a misleading $0 total */}
      {orders.length > 0 && (
      <div className="sticky bottom-4 bg-[#06539B] text-white rounded-2xl p-4 flex items-center justify-between shadow-xl shadow-black/20">
        <div>
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">
            {isEs ? 'Costo total estimado de materiales' : 'Total estimated material cost'}
          </div>
          <div className="text-[24px] font-[900] tracking-[-1px]">{formatMoneyValue(totalCost, isEs)}</div>
        </div>
        <div className="flex items-center gap-3">
          {/* PR #193: show issue/blocked count instead of order-now (always-0 for real projects) */}
          {(issueCount + blockedCount) > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-500 text-white text-[13px] font-[800] px-3 py-2 rounded-full animate-pulse">
              <AlertTriangle size={12} /> {issueCount + blockedCount} {isEs ? 'con problemas' : 'issues'}
            </div>
          )}
        </div>
      </div>
      )}

      {/* PR #189: create material order modal — real projects only */}
      {showCreateForm && !isDemo && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="mo-create-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[14px] font-[800] text-[#1C1A17]">
                {isEs ? 'Crear orden de material' : 'Create material order'}
              </h3>
              <button onClick={() => { setShowCreateForm(false); setCreateError(null) }} className="text-[#9E9890] hover:text-[#1C1A17]" aria-label="Close">✕</button>
            </div>

            <div className="space-y-3">
              {/* Item name */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Nombre del material *' : 'Item name *'}
                </label>
                <input
                  type="text"
                  value={createFormData.material}
                  onChange={e => setCreateFormData(p => ({ ...p, material: e.target.value }))}
                  placeholder={isEs ? 'Ej: Madera de encofrado' : 'e.g. Framing lumber'}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                  data-testid="mo-create-material"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Categoría *' : 'Category *'}
                </label>
                <select
                  value={createFormData.category}
                  onChange={e => setCreateFormData(p => ({ ...p, category: e.target.value }))}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                  data-testid="mo-create-category"
                >
                  {MATERIAL_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Quantity + Unit */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                    {isEs ? 'Cantidad *' : 'Quantity *'}
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    value={createFormData.quantity}
                    onChange={e => setCreateFormData(p => ({ ...p, quantity: e.target.value }))}
                    placeholder="0"
                    className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                    data-testid="mo-create-quantity"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                    {isEs ? 'Unidad *' : 'Unit *'}
                  </label>
                  <input
                    type="text"
                    list="mo-units-create"
                    value={createFormData.unit}
                    onChange={e => setCreateFormData(p => ({ ...p, unit: e.target.value }))}
                    placeholder={isEs ? 'Ej: LF, EA, SF' : 'e.g. LF, EA, SF'}
                    className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                    data-testid="mo-create-unit"
                  />
                  <datalist id="mo-units-create">
                    {MATERIAL_UNITS.map(u => <option key={u} value={u} />)}
                  </datalist>
                </div>
              </div>

              {/* Supplier (optional) */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Proveedor (opcional)' : 'Supplier (optional)'}
                </label>
                <select
                  value={createFormData.supplierContactId}
                  onChange={e => setCreateFormData(p => ({ ...p, supplierContactId: e.target.value }))}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                  data-testid="mo-create-supplier"
                >
                  <option value="">{isEs ? 'Sin proveedor' : 'No supplier'}</option>
                  {supplierContacts.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Needed date (optional) */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Fecha requerida (opcional)' : 'Needed by (optional)'}
                </label>
                <input
                  type="date"
                  value={createFormData.neededByDate}
                  onChange={e => setCreateFormData(p => ({ ...p, neededByDate: e.target.value }))}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                  data-testid="mo-create-needed-date"
                />
              </div>

              {/* Notes (optional) */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Notas (opcional)' : 'Notes (optional)'}
                </label>
                <textarea
                  value={createFormData.notes}
                  onChange={e => setCreateFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder={isEs ? 'Detalles adicionales…' : 'Additional details…'}
                  rows={2}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B] resize-none"
                  data-testid="mo-create-notes"
                />
              </div>

              {createError && (
                <p className="text-[11px] font-[700] text-red-600" data-testid="mo-create-error">{createError}</p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => { setShowCreateForm(false); setCreateError(null) }}
                  className="flex-1 py-2 px-4 border border-[#ECEAE4] rounded-full text-[12px] font-[700] text-[#9E9890] hover:bg-[#F8F7F4]"
                >
                  {isEs ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={handleCreateOrder}
                  disabled={createLoading}
                  className="flex-1 py-2 px-4 bg-[#06539B] text-white rounded-full text-[12px] font-[700] hover:bg-[#054a88] disabled:opacity-50"
                  data-testid="mo-create-submit"
                >
                  {createLoading ? (isEs ? 'Guardando…' : 'Saving…') : (isEs ? 'Crear orden' : 'Create order')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PR #192: edit material order modal — real projects only */}
      {showEditForm && !isDemo && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="mo-edit-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[14px] font-[800] text-[#1C1A17]">
                {isEs ? 'Editar orden de material' : 'Edit material order'}
              </h3>
              <button onClick={() => { setShowEditForm(false); setEditError(null) }} className="text-[#9E9890] hover:text-[#1C1A17]" aria-label="Close">✕</button>
            </div>

            <div className="space-y-3">
              {/* Item name */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Nombre del material *' : 'Item name *'}
                </label>
                <input
                  type="text"
                  value={editFormData.material}
                  onChange={e => setEditFormData(p => ({ ...p, material: e.target.value }))}
                  placeholder={isEs ? 'Ej: Madera de encofrado' : 'e.g. Framing lumber'}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                  data-testid="mo-edit-material"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Categoría *' : 'Category *'}
                </label>
                <select
                  value={editFormData.category}
                  onChange={e => setEditFormData(p => ({ ...p, category: e.target.value }))}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                  data-testid="mo-edit-category"
                >
                  {MATERIAL_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Quantity + Unit */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                    {isEs ? 'Cantidad *' : 'Quantity *'}
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    value={editFormData.quantity}
                    onChange={e => setEditFormData(p => ({ ...p, quantity: e.target.value }))}
                    placeholder="0"
                    className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                    data-testid="mo-edit-quantity"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                    {isEs ? 'Unidad *' : 'Unit *'}
                  </label>
                  <input
                    type="text"
                    list="mo-units-edit"
                    value={editFormData.unit}
                    onChange={e => setEditFormData(p => ({ ...p, unit: e.target.value }))}
                    placeholder={isEs ? 'Ej: LF, EA, SF' : 'e.g. LF, EA, SF'}
                    className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                    data-testid="mo-edit-unit"
                  />
                  <datalist id="mo-units-edit">
                    {MATERIAL_UNITS.map(u => <option key={u} value={u} />)}
                  </datalist>
                </div>
              </div>

              {/* Supplier (optional) */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Proveedor (opcional)' : 'Supplier (optional)'}
                </label>
                <select
                  value={editFormData.supplierContactId}
                  onChange={e => setEditFormData(p => ({ ...p, supplierContactId: e.target.value }))}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                  data-testid="mo-edit-supplier"
                >
                  <option value="">{isEs ? 'Sin proveedor' : 'No supplier'}</option>
                  {supplierContacts.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Needed date (optional) */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Fecha requerida (opcional)' : 'Needed by (optional)'}
                </label>
                <input
                  type="date"
                  value={editFormData.neededByDate}
                  onChange={e => setEditFormData(p => ({ ...p, neededByDate: e.target.value }))}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B]"
                  data-testid="mo-edit-needed-date"
                />
              </div>

              {/* Notes (optional) */}
              <div>
                <label className="block text-[11px] font-[700] text-[#9E9890] mb-1">
                  {isEs ? 'Notas (opcional)' : 'Notes (optional)'}
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={e => setEditFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder={isEs ? 'Detalles adicionales…' : 'Additional details…'}
                  rows={2}
                  className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[500] text-[#1C1A17] focus:outline-none focus:border-[#06539B] resize-none"
                  data-testid="mo-edit-notes"
                />
              </div>

              {editError && (
                <p className="text-[11px] font-[700] text-red-600" data-testid="mo-edit-error">{editError}</p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => { setShowEditForm(false); setEditError(null) }}
                  className="flex-1 py-2 px-4 border border-[#ECEAE4] rounded-full text-[12px] font-[700] text-[#9E9890] hover:bg-[#F8F7F4]"
                >
                  {isEs ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={handleEditOrder}
                  disabled={editLoading}
                  className="flex-1 py-2 px-4 bg-[#06539B] text-white rounded-full text-[12px] font-[700] hover:bg-[#054a88] disabled:opacity-50"
                  data-testid="mo-edit-submit"
                >
                  {editLoading ? (isEs ? 'Guardando…' : 'Saving…') : (isEs ? 'Guardar cambios' : 'Save changes')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
